import React from "react";
import { monitor, logError, METRIC_NAMES } from "src/utils/monitoring";

interface PropsType {
  children: JSX.Element;
}

/**
 * Simple class to manage the application initialization metric.
 *
 * Note this is meant as a demonstration of how to use an errory boundary, and
 * is almost certainly inadequate for a more complex application.
 *
 * In your actual application, you are likely not fully initialized until you have completed
 * your initial REST calls.  You will need to either integrate that logic here, or else move
 * the initialization metric elsewhere.
 *
 * For applications using a state manager (e.g. Redux) it may make more sense to determine if
 * your application is initialized from that layer instead.
 *
 * https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 */
class AppInitWrapper extends React.Component<PropsType> {
  componentDidMount() {
    monitor.count(METRIC_NAMES.APP_INITIALIZED, 1, { methodName: 'AppInitialization' });
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    monitor.count(METRIC_NAMES.ROOT_ERROR_BOUNDARY_ENCOUNTER, 1, { methodName: 'AppErrorBoundary' });

    info.componentStack
      ? logError(info.componentStack)
      : logError("AppInitWrapper error boundary encounter");

    // In your application you may want to update the UI; here we will re-throw and fail the whole app, after logging
    throw error;
  }

  render() {
    return this.props.children;
  }
}

export default AppInitWrapper;
